import React, { useState, useEffect, useCallback, useRef } from 'react';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import Toolbar from './components/Toolbar';
import FileGrid from './components/FileGrid';
import UploadModal from './components/UploadModal';
import CreateFolderModal from './components/CreateFolderModal';
import FilePreviewModal from './components/FilePreviewModal';
import RenameModal from './components/RenameModal';
import TagsModal from './components/TagsModal';
import FolderTagsModal from './components/FolderTagsModal';
import BulkTagsModal from './components/BulkTagsModal';
import ShareFileModal from './components/ShareFileModal';
import { useFiles } from './hooks/useFiles';
import { isTokenExpired, shouldRefreshToken, clearAllTokens } from './utils/tokenUtils';
import { preloadCategory2Tags } from './services/predefinedTagsCache';
import { fileService } from './services/api';

import { downloadMultipleFiles } from './utils/downloadUtils';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Configuración dinámica de URLs según el entorno
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const LOGOUT_URL = isLocalhost
  ? 'http://localhost:3000/logout'
  : 'https://eventos-audiovisual.buenosaires.gob.ar/logout';

const COGNITO_LOGOUT_URL = 
  `https://us-east-1gje4udc2u.auth.us-east-1.amazoncognito.com/logout?client_id=2rq374k38rqp0veo8r012v45c8&logout_uri=${encodeURIComponent(LOGOUT_URL)}`;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameFile, setRenameFile] = useState(null);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [tagsFile, setTagsFile] = useState(null);
  const [showFolderTagsModal, setShowFolderTagsModal] = useState(false);
  const [tagsFolder, setTagsFolder] = useState(null);
  const [showBulkTagsModal, setShowBulkTagsModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareFiles, setShareFiles] = useState([]);
  const [existingTempFolder, setExistingTempFolder] = useState(null);
  const [refreshTempFolders, setRefreshTempFolders] = useState(0);
  
  // Cargar carrito desde localStorage al iniciar
  const [tempFolderFiles, setTempFolderFiles] = useState(() => {
    try {
      const saved = localStorage.getItem('tempFolderFiles');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error cargando carrito desde localStorage:', error);
      return [];
    }
  });
  
  // Guardar carrito en localStorage cada vez que cambie
  useEffect(() => {
    try {
      localStorage.setItem('tempFolderFiles', JSON.stringify(tempFolderFiles));
    } catch (error) {
      console.error('Error guardando carrito en localStorage:', error);
    }
  }, [tempFolderFiles]);

  const {
    files,
    filteredFiles,
    loading: filesLoading,
    error,
    searchTerm,
    setSearchTerm,
    selectedType,
    setSelectedType,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    selectedFiles,
    stats,
    currentPath,
    navigationHistory,
    rootFoldersLoading,
    rootFoldersLoaded,
    createFolder,
    uploadFile,
    deleteFile,
    deleteMultipleFiles,
    deleteFolder,
    renameFile: renameFileFromHook,
    getFileUrl,
    toggleFileSelection,
    clearSelection,
    setError,
    navigateToFolder,
    navigateBack,
    navigateToPath,
    loadFiles,
    tags,
    tagsLoading,
    selectedTag,
    tagResultsLoading,
    tagResultsError,
    loadFilesByTag,
    clearTagFilter,
    loadTags,
    selectedFolderTag,
    folderTagResultsLoading,
    folderTagResultsError,
    loadFilesByFolderTag,
    clearFolderTagFilter
  } = useFiles(isAuthenticated);

  // Flag para evitar múltiples refreshes simultáneos
  const isRefreshing = useRef(false);

  // Función helper para extraer email del idToken si no está en sessionStorage
  // IMPORTANTE: Para el refresh token, Cognito usa el email como username
  const getEmailFromToken = () => {
    const storedEmail = sessionStorage.getItem('userEmail');
    if (storedEmail) return storedEmail;
    
    // Si no hay email guardado, extraerlo del idToken
    try {
      const idToken = sessionStorage.getItem('cognitoToken');
      if (idToken) {
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        // Para Cognito REFRESH_TOKEN_AUTH, necesitamos usar el mismo username que se usó para obtener el token
        // Normalmente es el email, pero puede ser cognito:username o sub
        const email = payload.email || payload['cognito:username'] || payload.sub;
        if (email) {
          sessionStorage.setItem('userEmail', email);
          console.log('📧 Email extraído del idToken y guardado:', email);
          return email;
        }
      }
    } catch (error) {
      console.error('Error extrayendo email del token:', error);
    }
    return null;
  };

  // Función mejorada para refrescar token con mejor manejo de errores
  // IMPORTANTE: Esta función debe estar definida ANTES de checkTokenExpiration
  const refreshToken = async () => {
    try {
      const storedRefreshToken = sessionStorage.getItem('refreshToken');
      const idToken = sessionStorage.getItem('cognitoToken');
      const email = getEmailFromToken();
      
      if (!storedRefreshToken || !email) {
        console.error('❌ No se encontraron refreshToken o email');
        handleLogout();
        return false;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: storedRefreshToken, email, idToken }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        sessionStorage.setItem('cognitoToken', data.tokens.idToken);
        sessionStorage.setItem('accessToken', data.tokens.accessToken);
        console.log('✅ Token refrescado exitosamente - sesión extendida');
        return true;
      } else {
        // Si el refresh token es inválido/expirado (después de 5 días)
        if (data.error === 'NotAuthorizedException' || 
            data.message?.includes('expired') || 
            data.message?.includes('Invalid refresh token')) {
          console.error('❌ Refresh token inválido o expirado (5 días) - cerrando sesión');
        } else {
          console.error('❌ Error refrescando token:', data.message);
        }
        handleLogout();
        return false;
      }
    } catch (error) {
      console.error('❌ Error de red al refrescar token:', error);
      // NO hacer logout inmediato por error de red - podría ser temporal
      // El próximo check (en 30 segundos) lo intentará de nuevo
      return false;
    }
  };

  // NOTA: Las funciones autoRefreshToken y checkTokenExpiration fueron eliminadas
  // porque el interceptor de Axios (en services/api.js) maneja automáticamente
  // el refresh de tokens cuando detecta un error 401. Esto es más eficiente
  // y evita race conditions con múltiples intervalos.

  // Prevenir navegación hacia atrás cuando la sesión está cerrada
  useEffect(() => {
    const handlePopState = (event) => {
      if (!isAuthenticated) {
        // Si no está autenticado, prevenir navegación hacia atrás
        event.preventDefault();
        window.history.pushState(null, '', window.location.href);
        // Forzar redirección al login si intentan navegar hacia atrás
        window.location.href = '/';
      }
    };

    // Agregar múltiples estados al historial para bloquear navegación hacia atrás
    if (!isAuthenticated) {
      // Limpiar historial existente
      window.history.replaceState(null, '', window.location.href);
      // Agregar múltiples estados para bloquear navegación hacia atrás
      for (let i = 0; i < 3; i++) {
        window.history.pushState(null, '', window.location.href);
      }
    }

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isAuthenticated]);

  // Prevenir caché del navegador
  useEffect(() => {
    // Configurar headers de no-cache dinámicamente
    if (isAuthenticated) {
      // Para páginas protegidas, asegurar no-cache
      const metaCache = document.querySelector('meta[http-equiv="Cache-Control"]');
      if (metaCache) {
        metaCache.setAttribute('content', 'no-cache, no-store, must-revalidate');
      }
    }
  }, [isAuthenticated]);

  // Verificar si estamos en la ruta /logout (callback de Cognito)
  useEffect(() => {
    if (window.location.pathname === '/logout') {
      console.log('🔄 Detectado callback de logout desde Cognito');
      clearAllTokens();
      setIsAuthenticated(false);
      
      // Limpiar historial
      window.history.replaceState(null, '', window.location.href);
      
      // Limpiar todas las cookies del navegador
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
      });
      
      // Limpiar localStorage y sessionStorage
      localStorage.clear();
      sessionStorage.clear();
      
      console.log('🧹 Sesión completamente limpiada desde callback de logout');
      
      // Redirigir al login
      window.location.href = '/';
    }
  }, []);

  // Verificar si el usuario está autenticado al cargar la app
  // El interceptor de Axios manejará automáticamente el refresh cuando sea necesario
  useEffect(() => {
    checkAuthState();
    console.log('✅ Verificación inicial de autenticación completada. El interceptor de Axios manejará el refresh automático.');
  }, []);

  // Refresh preventivo de tokens para evitar que expiren silenciosamente
  // Se ejecuta cada 10 minutos y refresca si el token expira en los próximos 3 minutos
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // Función para refresh preventivo
    const performPreventiveRefresh = async () => {
      const token = sessionStorage.getItem('cognitoToken');
      if (!token) return;
      
      // Solo refrescar si el token expira en los próximos 3 minutos
      if (shouldRefreshToken(token, 3)) {
        // Evitar múltiples refreshes simultáneos
        if (isRefreshing.current) {
          console.log('⏭️ Refresh ya en curso, omitiendo refresh preventivo...');
          return;
        }
        
        console.log('🔄 Refresh preventivo: token próximo a expirar');
        isRefreshing.current = true;
        try {
          await refreshToken();
        } finally {
          isRefreshing.current = false;
        }
      }
    };
    
    // Ejecutar inmediatamente al montar
    performPreventiveRefresh();
    
    // Y luego cada 10 minutos (antes de que expire el token de 15 min)
    const interval = setInterval(performPreventiveRefresh, 10 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Precargar tags predefinidos de categoría 2 cuando el usuario se autentica
  useEffect(() => {
    if (isAuthenticated) {
      preloadCategory2Tags();
    }
  }, [isAuthenticated]);

  const checkAuthState = async () => {
    try {
      const token = sessionStorage.getItem('cognitoToken');
      const accessToken = sessionStorage.getItem('accessToken');
      const storedRefreshToken = sessionStorage.getItem('refreshToken');
      
      // Si no hay tokens, desautenticar
      if (!token || !accessToken || !storedRefreshToken) {
        setIsAuthenticated(false);
        clearAllTokens();
        localStorage.clear();
        window.history.replaceState(null, '', window.location.href);
        setLoading(false);
        return;
      }
      
      // Si el token está expirado, intentar refrescar ANTES de hacer logout
      if (isTokenExpired(token)) {
        console.log('🔐 Token expirado en checkAuthState - intentando refrescar');
        if (storedRefreshToken && !isRefreshing.current) {
          isRefreshing.current = true;
          const refreshed = await refreshToken();
          isRefreshing.current = false;
          
          if (refreshed) {
            // Token refrescado exitosamente, continuar autenticado
            console.log('✅ Token refrescado en checkAuthState - sesión continua');
        setIsAuthenticated(true);
            setLoading(false);
            return;
          }
        }
        // Si no se pudo refrescar, hacer logout
        console.log('❌ No se pudo refrescar token en checkAuthState - cerrando sesión');
        setIsAuthenticated(false);
        clearAllTokens();
        localStorage.clear();
        window.history.replaceState(null, '', window.location.href);
        setLoading(false);
        return;
      }
      
      // Token válido, autenticar
      setIsAuthenticated(true);
    } catch (err) {
      console.error('Error verificando estado de autenticación:', err);
      setIsAuthenticated(false);
      clearAllTokens();
      localStorage.clear();
      window.history.replaceState(null, '', window.location.href);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = useCallback((token) => {
    setIsAuthenticated(true);
    // Limpiar historial para prevenir navegación hacia atrás
    window.history.replaceState(null, '', window.location.href);
  }, []);

  const handleLogout = () => {
    console.log('🚪 Iniciando proceso de logout...');
    
    // Limpiar completamente el estado de autenticación
    setIsAuthenticated(false);
    
    // Limpiar TODOS los tokens inmediatamente
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("cognitoToken");
    sessionStorage.removeItem("refreshToken");
    sessionStorage.removeItem("userEmail");
    
    // Limpiar también los códigos de autorización usados
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('cognito_code_used_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    // Limpiar localStorage también por seguridad
    localStorage.clear();
    
    // Limpiar todas las cookies del navegador
    document.cookie.split(";").forEach(function(c) { 
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
    });
    
    console.log('🧹 Tokens y datos limpiados completamente');
    
    // Limpiar completamente el historial del navegador
    window.history.replaceState(null, '', window.location.href);
    
    // Agregar múltiples estados al historial para bloquear navegación hacia atrás
    for (let i = 0; i < 5; i++) {
      window.history.pushState(null, '', window.location.href);
    }
    
    // Escuchar eventos de popstate y prevenir navegación
    const handlePopState = (event) => {
      event.preventDefault();
      window.history.pushState(null, '', window.location.href);
      // Forzar redirección al login si intentan navegar hacia atrás
      window.location.href = '/';
    };
    
    window.addEventListener('popstate', handlePopState);
    
    // Pequeña pausa para asegurar que la limpieza se complete
    setTimeout(() => {
      console.log('🔄 Redirigiendo a Cognito logout...');
      // Redirigir a Cognito Hosted UI logout
      window.location.href =
        "https://us-east-1gje4udc2u.auth.us-east-1.amazoncognito.com/logout" +
        "?client_id=2rq374k38rqp0veo8r012v45c8" +
        "&logout_uri=https://eventos-audiovisual.buenosaires.gob.ar/logout";
    }, 100);
  };

  // Handlers para la barra lateral
  const handleTypeChange = (type) => {
    setSelectedType(type);
    clearSelection();
  };

  // Handlers para la toolbar
  const handleSortChange = (newSortBy, newSortOrder) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };

  // Handlers para acciones de archivos
  const handleUploadFile = async (fileName, base64Data, mimeType, tags = null, onUploadProgress = null) => {
    try {
      // Pasar false para no recargar archivos después de cada subida
      const success = await uploadFile(fileName, base64Data, mimeType, tags, false, onUploadProgress);
      // NO cerrar el modal ni mostrar toast aquí - el modal lo manejará al final
      return success;
    } catch (error) {
      console.error('Error subiendo archivo:', error);
      // NO mostrar toast aquí - el modal mostrará el error al final
      return false;
    }
  };

  const handleCreateFolder = async (folderName, tags = null) => {
    try {
      const success = await createFolder(folderName, tags);
      if (success) {
        setShowCreateFolderModal(false);
        // Forzar recarga del catálogo de tags de carpetas si se creó con tags
        if (tags && tags.length > 0) {
          setRefreshFolderTags(prev => prev + 1);
        }
        toast.success(`Carpeta "${folderName}" creada con éxito`, { autoClose: 2000 });
      }
      return success;
    } catch (error) {
      console.error('Error creando carpeta:', error);
      toast.error(`Error al crear la carpeta: ${error.message}`, { autoClose: 2000 });
      return false;
    }
  };

  const handleFileDownload = async (file) => {
    try {
      const fileName = file.Key || file.name;
      const url = await getFileUrl(fileName, true);
      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
      }
    } catch (error) {
      console.error('Error descargando archivo:', error);
      setError('Error al descargar el archivo');
    }
  };

  const handleFileDelete = async (file) => {
    const fileName = file.Key || file.name;
    const isFolder = fileName.endsWith('/') || file.isVirtualFolder;
    const itemType = isFolder ? 'carpeta' : 'archivo';
    
    if (window.confirm(`¿Estás seguro de que quieres eliminar esta ${itemType}?`)) {
      try {
        if (isFolder) {
          // Si es carpeta, usar deleteFolder
          const folderPath = fileName.endsWith('/') ? fileName.slice(0, -1) : fileName;
          const success = await deleteFolder(folderPath);
          if (success) {
            toast.success(`Carpeta "${folderPath}" eliminada con éxito`, { autoClose: 2000 });
          }
        } else {
          // Si es archivo, usar deleteFile
          const success = await deleteFile(fileName);
          if (success) {
            toast.success(`Archivo "${fileName}" eliminado con éxito`, { autoClose: 2000 });
          }
        }
      } catch (error) {
        console.error(`Error eliminando ${itemType}:`, error);
        toast.error(`Error al eliminar la ${itemType}: ${error.message}`, { autoClose: 2000 });
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedFiles.length === 0) return;
    if (window.confirm(`¿Estás seguro de que quieres eliminar ${selectedFiles.length} archivo(s)?`)) {
      try {
        const result = await deleteMultipleFiles(selectedFiles);
        if (result) {
          toast.success(`${selectedFiles.length} archivo(s) eliminados con éxito`, { autoClose: 2000 });
        } else {
          toast.error('No se pudieron eliminar los archivos seleccionados', { autoClose: 2000 });
        }
      } catch (error) {
        console.error('Error eliminando archivos:', error);
        toast.error('Error al eliminar los archivos seleccionados', { autoClose: 2000 });
      }
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedFiles.length === 0) return;
    
    try {
      const filesToDownload = filteredFiles.filter(file => 
        selectedFiles.includes(file.Key || file.name)
      );
      
      await downloadMultipleFiles(filesToDownload, getFileUrl);
    } catch (error) {
      console.error('Error descargando archivos:', error);
      setError('Error al descargar los archivos');
    }
  };

  const handleTagSelected = () => {
    if (selectedFiles.length === 0) return;
    setShowBulkTagsModal(true);
  };

  const handleBulkTagsUpdated = async () => {
    try {
      // Limpiar selección de archivos
      clearSelection();
      
      // Limpiar caché del frontend para forzar recarga fresca
      fileService.clearCache(currentPath);
      
      // Usar setTimeout para asegurar que la recarga se ejecute después de cualquier efecto pendiente
      // Esto evita conflictos con el useEffect que observa currentPath
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Recargar archivos y tags después de actualizar
      await loadFiles(currentPath, true);
      await loadTags();
      
      // NO recargar con filtro de tag - dejar que el usuario vea todos los archivos actualizados
      // Si el usuario quiere ver solo los archivos con un tag específico, puede volver a seleccionarlo
    } catch (error) {
      console.error('Error recargando archivos después de actualizar tags:', error);
      toast.error('Error al recargar archivos', { autoClose: 2000 });
    }
  };

  const handleFilePreview = (file) => {
    setPreviewFile(file);
    setShowFilePreview(true);
  };

  const handleFileRename = (file) => {
    setRenameFile(file);
    setShowRenameModal(true);
  };

  const handleFileTags = (file) => {
    setTagsFile(file);
    setShowTagsModal(true);
  };

  const handleTagsUpdated = async (fileName, newTags) => {
    try {
      // Limpiar selección de archivos (por si acaso)
      clearSelection();
      
      // Limpiar caché del frontend para forzar recarga fresca
      fileService.clearCache(currentPath);
      
      // Usar setTimeout para asegurar que la recarga se ejecute después de cualquier efecto pendiente
      // Esto evita conflictos con el useEffect que observa currentPath
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Recargar archivos para actualizar la vista
      await loadFiles(currentPath, true);
      await loadTags();
      toast.success(`Tags actualizados correctamente`, { autoClose: 2000 });
    } catch (error) {
      console.error('Error recargando archivos después de actualizar tags:', error);
      toast.error('Error al recargar archivos', { autoClose: 2000 });
    }
  };

  const handleFolderTags = (folder) => {
    setTagsFolder(folder);
    setShowFolderTagsModal(true);
  };

  // Función para compartir un archivo individual
  const handleShareFile = (file) => {
    setShareFiles([file]);
    setShowShareModal(true);
  };

  // Función para agregar archivos seleccionados al carrito de carpeta temporal
  const handleAddToTempFolder = async () => {
    if (selectedFiles.length === 0) {
      toast.warning('No hay archivos seleccionados', { autoClose: 2000 });
      return;
    }
    
    const selectedItems = filteredFiles.filter(file => 
      selectedFiles.includes(file.Key || file.name)
    );
    
    if (selectedItems.length === 0) {
      toast.warning('No se encontraron archivos para agregar', { autoClose: 2000 });
      return;
    }
    
    // Separar archivos normales de carpetas ANTES de procesar
    const regularFiles = selectedItems.filter(f => {
      const key = f.Key || f.name || '';
      return key && !key.endsWith('/') && f.type !== 'folder';
    });
    
    const folders = selectedItems.filter(f => {
      const key = f.Key || f.name || '';
      return key && (key.endsWith('/') || f.type === 'folder');
    });
    
    let filesToAdd = [...regularFiles];
    
    // Si hay carpetas, validar y expandir cada una
    if (folders.length > 0) {
      const expandedFiles = [];
      let hasError = false;
      
      for (const folder of folders) {
        const folderPath = folder.Key || folder.name;
        if (!folderPath) continue;
        
        // Normalizar el path: remover el / final si existe
        const normalizedPath = folderPath.endsWith('/') 
          ? folderPath.slice(0, -1) 
          : folderPath;
        
        try {
          console.log(`🔍 Validando carpeta: "${normalizedPath}"`);
          
          // Obtener archivos de la carpeta
          const response = await fileService.getFiles(normalizedPath);
          const folderFiles = response.data || []; // ✅ CORRECCIÓN: usar response.data
          
          if (!folderFiles || folderFiles.length === 0) {
            toast.warning(
              `La carpeta "${normalizedPath}" está vacía`,
              { autoClose: 3000 }
            );
            continue;
          }
          
          // Normalizar el path con / al final para cálculos correctos
          const normalizedPathWithSlash = normalizedPath.endsWith('/') 
            ? normalizedPath 
            : normalizedPath + '/';
          
          // Separar archivos directos de subcarpetas
          const directFiles = folderFiles.filter(f => {
            const key = f.Key || f.name || '';
            if (!key || key.endsWith('/')) {
              return false; // Saltar carpetas
            }
            
            // Verificar que el archivo pertenece a esta carpeta
            if (!key.startsWith(normalizedPathWithSlash)) {
              return false;
            }
            
            const relativePath = key.substring(normalizedPathWithSlash.length);
            
            // Archivo directo: tiene relativePath, no contiene más slashes
            return relativePath && !relativePath.includes('/');
          });
          
          // Detectar subcarpetas de forma más eficiente
          const subfolderKeys = new Set();
          folderFiles.forEach(f => {
            const key = f.Key || f.name || '';
            if (!key || !key.startsWith(normalizedPathWithSlash)) return;
            
            const relativePath = key.substring(normalizedPathWithSlash.length);
            if (relativePath && relativePath.includes('/')) {
              const firstLevel = relativePath.split('/')[0];
              if (firstLevel) {
                subfolderKeys.add(firstLevel);
              }
            }
          });
          
          const hasSubfolders = subfolderKeys.size > 0;
          
          console.log(`📊 Carpeta "${normalizedPath}": ${directFiles.length} archivos directos, ${subfolderKeys.size} subcarpetas`);
          
          // REGLA: Si solo tiene subcarpetas, mostrar error y no agregar nada
          if (directFiles.length === 0 && hasSubfolders) {
            toast.error(
              `❌ La carpeta "${normalizedPath}" solo contiene subcarpetas y no se puede agregar.\n\nSelecciona archivos individuales o comparte cada subcarpeta por separado.`,
              { 
                autoClose: 6000,
                style: { whiteSpace: 'pre-line' }
              }
            );
            hasError = true;
            break; // No agregar nada si hay una carpeta inválida
          }
          
          // Si tiene archivos directos, agregarlos a la lista expandida
          if (directFiles.length > 0) {
            expandedFiles.push(...directFiles);
            toast.info(
              `📁 Carpeta "${normalizedPath}" expandida: ${directFiles.length} archivo(s)`,
              { autoClose: 3000 }
            );
          } else if (!hasSubfolders) {
            // Si no tiene archivos directos ni subcarpetas, está vacía
            toast.warning(
              `La carpeta "${normalizedPath}" no tiene archivos para agregar`,
              { autoClose: 3000 }
            );
          }
        } catch (error) {
          console.error('❌ Error validando carpeta:', error);
          console.error('Error stack:', error.stack);
          toast.error(
            `Error validando carpeta "${normalizedPath}": ${error.message}`,
            { autoClose: 3000 }
          );
          hasError = true;
          break;
        }
      }
      
      if (hasError) {
        return; // No agregar nada si hubo un error
      }
      
      // Combinar archivos regulares + archivos expandidos (NO las carpetas originales)
      filesToAdd = [...regularFiles, ...expandedFiles];
    } else {
      // Si no hay carpetas, usar solo los archivos regulares
      filesToAdd = regularFiles;
    }
    
    // Validación final: asegurar que solo hay archivos (no carpetas)
    filesToAdd = filesToAdd.filter(f => {
      const key = f.Key || f.name || '';
      return key && !key.endsWith('/');
    });
    
    if (filesToAdd.length === 0) {
      toast.warning('No hay archivos válidos para agregar', { autoClose: 2000 });
      return;
    }
    
    // Agregar al carrito (evitar duplicados)
    // IMPORTANTE: Comparar solo con archivos que ya están en tempFolderFiles (no carpetas)
    setTempFolderFiles(prev => {
      // Crear Set de keys existentes, filtrando carpetas
      const existingKeys = new Set(
        prev
          .map(f => {
            const key = f.Key || f.name || '';
            return key && !key.endsWith('/') ? key : null;
          })
          .filter(k => k !== null)
      );
      
      // Filtrar archivos que ya existen
      const newFiles = filesToAdd.filter(f => {
        const key = f.Key || f.name || '';
        return key && !existingKeys.has(key);
      });
      
      if (newFiles.length === 0) {
        toast.info('Los archivos seleccionados ya están en la carpeta temporal', { autoClose: 2000 });
        return prev;
      }
      
      toast.success(`${newFiles.length} archivo(s) agregado(s) a carpeta temporal`, { autoClose: 2000 });
      return [...prev, ...newFiles];
    });
    
    clearSelection(); // Limpiar selección después de agregar
  };

  // Función para crear carpeta temporal
  const handleCreateTempFolder = async () => {
    // Prioridad 1: Si hay archivos en el carrito, usarlos
    if (tempFolderFiles.length > 0) {
      setShareFiles(tempFolderFiles);
      setExistingTempFolder(null);
      setShowShareModal(true);
    } 
    // Prioridad 2: Si hay archivos seleccionados actualmente, usarlos
    else if (selectedFiles.length > 0) {
      const filesToShare = filteredFiles.filter(file => 
        selectedFiles.includes(file.Key || file.name)
      );
      setShareFiles(filesToShare);
      setExistingTempFolder(null);
      setShowShareModal(true);
    } 
    // Prioridad 3: Crear carpeta temporal vacía
    else {
      setShareFiles([]);
      setExistingTempFolder(null);
      setShowShareModal(true);
      toast.info('Crea la carpeta temporal y luego selecciona archivos para agregarlos', { autoClose: 3000 });
    }
  };

  // Función cuando se completa el compartir
  const handleShareComplete = (result) => {
    // Limpiar selección después de compartir
    clearSelection();
    // Limpiar carrito de archivos temporales (también de localStorage)
    setTempFolderFiles([]);
    try {
      localStorage.removeItem('tempFolderFiles');
    } catch (error) {
      console.error('Error limpiando carrito de localStorage:', error);
    }
    // Refrescar la lista de carpetas temporales en el sidebar
    setRefreshTempFolders(prev => prev + 1);
    console.log('Compartir completado:', result);
  };

  // Función para gestionar una carpeta temporal existente
  const handleManageTempFolder = (folder) => {
    setExistingTempFolder(folder);
    setShareFiles([]);
    setShowShareModal(true);
  };

  const [refreshFolderTags, setRefreshFolderTags] = useState(0);
  
  const handleFolderTagsUpdated = async (folderName, newTags) => {
    try {
      // Recargar archivos para actualizar la vista
      await loadFiles(currentPath, true);
      // Forzar recarga del catálogo de tags de carpetas en el Sidebar
      setRefreshFolderTags(prev => prev + 1);
      toast.success(`Tags de carpeta actualizados correctamente`, { autoClose: 2000 });
    } catch (error) {
      console.error('Error recargando archivos después de actualizar tags de carpeta:', error);
    }
  };

  const handleRenameConfirm = async (oldFileName, newFileName) => {
    try {
      const success = await renameFileFromHook(oldFileName, newFileName);
      if (success) {
        setShowRenameModal(false);
        setRenameFile(null);
      }
      return success;
    } catch (error) {
      console.error('Error renombrando archivo:', error);
      return false;
    }
  };

  // Mostrar spinner mientras verifica autenticación
  if (loading) {
    return (
      <div className="min-h-screen bg-main-bg flex items-center justify-center">
        <div className="text-white text-xl">Cargando...</div>
      </div>
    );
  }

  // Mostrar login si no está autenticado
  if (!isAuthenticated) {
    // Limpiar cualquier dato residual y prevenir acceso
    clearAllTokens();
    localStorage.clear();
    
    // Limpiar historial para prevenir navegación hacia atrás
    window.history.replaceState(null, '', window.location.href);
    
    return <Login onLogin={handleLogin} />;
  }

  // Mostrar la app principal si está autenticado
  return (
    <div className="h-screen bg-main-bg flex">
      <ToastContainer position="top-right" autoClose={2000} hideProgressBar newestOnTop closeOnClick pauseOnFocusLoss={false} pauseOnHover={false} />
      {/* Sidebar */}
      <Sidebar
        selectedType={selectedType}
        onTypeChange={handleTypeChange}
        stats={stats}
        onCreateFolder={() => setShowCreateFolderModal(true)}
        onUploadFiles={() => setShowUploadModal(true)}
        onFolderOpen={navigateToFolder}
        currentPath={currentPath}
        tags={tags}
        tagsLoading={tagsLoading}
        selectedTag={selectedTag}
        onTagSelect={loadFilesByTag}
        onClearTag={clearTagFilter}
        selectedFolderTag={selectedFolderTag}
        onFolderTagSelect={loadFilesByFolderTag}
        onClearFolderTag={clearFolderTagFilter}
        refreshFolderTags={refreshFolderTags}
        refreshTempFolders={refreshTempFolders}
        onLogout={handleLogout}
        onManageTempFolder={handleManageTempFolder}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Toolbar */}
        <Toolbar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          selectedFiles={selectedFiles}
          onCreateFolder={() => setShowCreateFolderModal(true)}
          onUploadFiles={() => setShowUploadModal(true)}
          onDeleteSelected={handleDeleteSelected}
          onDownloadSelected={handleDownloadSelected}
          onTagSelected={handleTagSelected}
          selectedTag={selectedTag}
          onClearTag={clearTagFilter}
          tagResultsLoading={tagResultsLoading}
          tagResultsError={tagResultsError}
          selectedFolderTag={selectedFolderTag}
          onClearFolderTag={clearFolderTagFilter}
          folderTagResultsLoading={folderTagResultsLoading}
          folderTagResultsError={folderTagResultsError}
          onLogout={handleLogout}
          onCreateTempFolder={handleCreateTempFolder}
          onAddToTempFolder={handleAddToTempFolder}
          tempFolderFilesCount={tempFolderFiles.length}
        />

        {/* File Grid */}
        <div className="flex-1 overflow-auto scrollbar-thin">
          <FileGrid
            files={filteredFiles}
            loading={filesLoading}
            error={error}
            selectedFiles={selectedFiles}
            onFileSelect={toggleFileSelection}
            onFileDownload={handleFileDownload}
            onFileDelete={handleFileDelete}
            onFilePreview={handleFilePreview}
            onFileRename={handleFileRename}
            onFileTags={handleFileTags}
            onFolderTags={handleFolderTags}
            currentPath={currentPath}
            navigationHistory={navigationHistory}
            onFolderOpen={navigateToFolder}
            onNavigateBack={navigateBack}
            onNavigateToPath={navigateToPath}
            onShareFile={handleShareFile}
            rootFoldersLoading={rootFoldersLoading}
            rootFoldersLoaded={rootFoldersLoaded}
            isSearching={searchTerm.trim().length > 0}
            searchTerm={searchTerm}
            selectedTag={selectedTag}
            tagResultsLoading={tagResultsLoading}
            tagResultsError={tagResultsError}
            selectedFolderTag={selectedFolderTag}
            folderTagResultsLoading={folderTagResultsLoading}
            folderTagResultsError={folderTagResultsError}
          />
        </div>
      </div>

      {/* Modals */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUploadFile}
        onUploadComplete={async () => {
          await loadFiles(currentPath, true);
          if (selectedTag) {
            await loadFilesByTag(selectedTag);
          }
          await loadTags();
        }}
        currentPath={currentPath}
      />

      <CreateFolderModal
        isOpen={showCreateFolderModal}
        onClose={() => setShowCreateFolderModal(false)}
        onCreateFolder={handleCreateFolder}
      />

      <FilePreviewModal
        isOpen={showFilePreview}
        onClose={() => setShowFilePreview(false)}
        file={previewFile}
      />

      <RenameModal
        isOpen={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        file={renameFile}
        onRename={handleRenameConfirm}
      />

      <TagsModal
        isOpen={showTagsModal}
        onClose={() => setShowTagsModal(false)}
        file={tagsFile}
        onTagsUpdated={handleTagsUpdated}
      />

      <FolderTagsModal
        isOpen={showFolderTagsModal}
        onClose={() => setShowFolderTagsModal(false)}
        folder={tagsFolder}
        onTagsUpdated={handleFolderTagsUpdated}
      />

      <BulkTagsModal
        isOpen={showBulkTagsModal}
        onClose={() => setShowBulkTagsModal(false)}
        files={filteredFiles.filter(file => selectedFiles.includes(file.Key || file.name))}
        onTagsUpdated={handleBulkTagsUpdated}
      />

      <ShareFileModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setShareFiles([]);
          setExistingTempFolder(null);
        }}
        files={shareFiles}
        onShareComplete={handleShareComplete}
        existingFolder={existingTempFolder}
        onFilesUpdated={(updatedFiles) => {
          // Sincronizar tempFolderFiles con los archivos actualizados del modal
          // Convertir las keys a objetos con la misma estructura que tempFolderFiles
          const updatedFilesObjects = updatedFiles.map(key => {
            // Buscar el archivo original en tempFolderFiles o en shareFiles
            const originalFile = tempFolderFiles.find(f => {
              const fileKey = f.Key || f.name || '';
              return fileKey === key;
            }) || shareFiles.find(f => {
              const fileKey = f.Key || f.name || '';
              return fileKey === key;
            }) || { Key: key, name: key.split('/').pop() };
            return originalFile;
          });
          setTempFolderFiles(updatedFilesObjects);
          // También actualizar shareFiles para mantener consistencia
          setShareFiles(updatedFilesObjects);
        }}
      />
    </div>
  );
}

export default App; 